import { useEffect, useState, SyntheticEvent, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardHeader,
    Collapse,
    Divider,
    Grid,
    Skeleton,
    Stack,
    Tab,
    TablePagination,
    Tabs,
    Typography,
    useTheme
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Brightness1Icon from '@mui/icons-material/Brightness1';

import MainCard from 'ui-component/cards/MainCard';
import { ApexOptions } from 'apexcharts';
import AccountCircleTwoTone from '@mui/icons-material/AccountCircleTwoTone';
import SideIconCard from './components/SideIconCard';
import ReactApexChart from 'react-apexcharts';
import { options } from 'widget/Chart/revenue-chart';

import { getZeroLevel } from 'store/slices/healthContent';
import { useDispatch, useSelector } from 'store';
import TableUpdates from './TableUpdates';
import MultiCatalog from './MultiCatalog';

import { MerchantsSelector, SingleMerchantSelector } from 'ui-component/MultiMerchantButton';
import { TabsProps } from '../../../../types/index';
import { FormattedMessage, useIntl } from 'react-intl';

import { gridSpacing } from 'store/constant';
import DownloadUpdates from './DownloadUpdates';
import SummaryCard from './components/SummaryCard';
import { MerchantType } from 'types/components/shared';
import { HCHealthContent } from 'types/slices/slices';

function TabPanel({ children, value, index, ...other }: TabsProps) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <>{children}</>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

const FirstLevel = () => {
    type MerchantsSelectorValue = MerchantType[] | null;
    type SingleMerchantSelectorValue = MerchantType | null;
    const [firstLev, setFirstLevel] = useState<HCHealthContent[]>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [value, setValue] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(2);
    const [selectedMerchant, setSelectedMerchant] = useState<MerchantType | MerchantType[] | null>(null);
    const [multiHealth, setMultiHealth] = useState<HCHealthContent[]>([]);
    const handleChange = (event: SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
    const theme = useTheme();

    const dispatch = useDispatch();
    const { firstLevel } = useSelector((state) => state.healthContent);

    const intl = useIntl();

    useEffect(() => {
        if (firstLev?.length && selectedMerchant) {
            let filteredMerchants: HCHealthContent[] = [];

            // Verificar si selectedMerchant es un array
            if (Array.isArray(selectedMerchant)) {
                filteredMerchants = firstLev
                    ?.filter(
                        (merchant: HCHealthContent) =>
                            selectedMerchant?.some((item: MerchantType) => item.merchantId === merchant.merchantId)
                    )
                    .map((merchant: HCHealthContent) => {
                        const matchingFilter = selectedMerchant?.find((item: MerchantType) => item.merchantId === merchant.merchantId);
                        return {
                            ...merchant,
                            name: matchingFilter ? matchingFilter.name : ''
                        };
                    });
            } else {
                filteredMerchants = firstLev
                    .filter((merchant: HCHealthContent) => merchant.merchantId === selectedMerchant.merchantId)
                    .map((merchant: HCHealthContent) => ({
                        ...merchant,
                        name: selectedMerchant.name
                    }));
            }

            setMultiHealth(filteredMerchants);
        }
    }, [selectedMerchant, firstLev]);

    const optionsBars = (metric: any) => {
        const newOpt: ApexOptions = {
            ...options,
            // eslint-disable-next-line no-nested-ternary
            colors: metric?.percentage <= 34 ? ['#F44336'] : metric.percentage >= 35 && metric.percentage <= 80 ? ['#f17502'] : ['#02f13a'],
            labels: [metric.description],
            plotOptions: {
                radialBar: {
                    dataLabels: {
                        value: {
                            color: '#d7dcec',
                            offsetY: 5
                        }
                    }
                }
            }
        };
        return newOpt;
    };

    useEffect(() => {
        setFirstLevel(firstLevel);
        setIsLoading(false);
    }, [firstLevel]);

    useEffect(() => {
        setIsLoading(true);
        dispatch(getZeroLevel());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        if (event?.target.value) setRowsPerPage(parseInt(event?.target.value, 10));
        setPage(0);
    };

    return (
        <MainCard
            sx={{
                overflow: 'initial'
            }}
            title={
                <Grid container alignItems="center" justifyContent="start" spacing={gridSpacing}>
                    <Grid item display="flex" alignItems="center">
                        <Typography variant="h3" sx={{ mr: 1 }}>
                            {' '}
                            <FormattedMessage id="health-content" />{' '}
                        </Typography>
                        {value === 0 && (
                            <SingleMerchantSelector
                                value={selectedMerchant as SingleMerchantSelectorValue}
                                onChange={(merchantsId) => {
                                    setSelectedMerchant(merchantsId);
                                }}
                            />
                        )}
                        {value === 1 && (
                            <MerchantsSelector
                                value={Array.isArray(selectedMerchant) ? selectedMerchant : ([selectedMerchant] as MerchantsSelectorValue)}
                                onChange={(merchantsId) => {
                                    setSelectedMerchant(merchantsId);
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
            }
            content={false}
        >
            <Stack sx={{ py: 1 }}>
                <Stack sx={{ px: 2 }}>
                    <Tabs value={value} variant="scrollable" onChange={handleChange} textColor="secondary" indicatorColor="secondary">
                        <Tab component={Link} to="#" label={<FormattedMessage id="general" />} {...a11yProps(0)} />
                        <Tab component={Link} to="#" label={<FormattedMessage id="multi-catalogue" />} {...a11yProps(1)} />
                    </Tabs>
                </Stack>

                <Collapse in={isLoading}>
                    <Grid container sx={{ px: 2 }} columnSpacing={2} rowSpacing={0}>
                        <Grid item xs={12} md={6} sx={{ p: 0 }}>
                            <Skeleton
                                sx={{
                                    height: {
                                        xs: 100,
                                        md: 250
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} sx={{ p: 0 }}>
                            <Skeleton
                                sx={{
                                    height: {
                                        xs: 100,
                                        md: 250
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Collapse>

                <Collapse in={!isLoading}>
                    <Stack sx={{ p: 2 }}>
                        <TabPanel value={value} index={0}>
                            {multiHealth &&
                                multiHealth.map((first: any, index: number) => (
                                    <Box key={`multiHealth-${index}`}>
                                        <TabPanel value={value} index={0}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2} alignItems="stretch">
                                                        <Grid item md={6} sm={12}>
                                                            <SideIconCard
                                                                iconPrimary={AccountCircleTwoTone}
                                                                primary="2,672"
                                                                secondary="Last week"
                                                                metrics={first}
                                                                name={first?.name}
                                                            />
                                                        </Grid>
                                                        <Grid item md={6} sm={12}>
                                                            <SummaryCard data={first} />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item lg={4} xs={12}>
                                                    <Grid container spacing={2}>
                                                        {first?.metricRange?.map((metric: any, i: number) => (
                                                            <Grid item xs={12} md={6} lg={12} key={i}>
                                                                <Card
                                                                    key={i}
                                                                    sx={{
                                                                        mb: 1,
                                                                        background:
                                                                            theme.palette.mode === 'dark'
                                                                                ? theme.palette.dark.main
                                                                                : theme.palette.grey[50],
                                                                        border: '1px solid',
                                                                        borderColor:
                                                                            theme.palette.mode === 'dark'
                                                                                ? 'transparent'
                                                                                : theme.palette.grey[100],
                                                                        '&:hover': {
                                                                            border: `1px solid${theme.palette.primary.main}`
                                                                        }
                                                                    }}
                                                                >
                                                                    <CardActionArea
                                                                        component={Link}
                                                                        to={`${metric?.typeDescription?.toLowerCase()}?idMerchant=${first?.merchantId}`}
                                                                    >
                                                                        <CardContent
                                                                            sx={{
                                                                                p: 0,
                                                                                '& svg': {
                                                                                    width: 'auto',
                                                                                    height: 150
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Grid container alignItems="center" spacing={0}>
                                                                                <Grid item xs={6} sx={{ p: 3 }}>
                                                                                    <Grid
                                                                                        container
                                                                                        justifyContent="space-between"
                                                                                        direction="column"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <Grid item sm={12}>
                                                                                            <Typography variant="h3" color="inherit">
                                                                                                {intl.formatMessage({
                                                                                                    id: `${metric.typeDescription}`
                                                                                                })}
                                                                                            </Typography>
                                                                                        </Grid>
                                                                                        <Grid item sm={11}>
                                                                                            <Stack
                                                                                                direction="row"
                                                                                                alignItems="center"
                                                                                                spacing={0.5}
                                                                                                sx={{ my: 1.75, mx: 'auto' }}
                                                                                            >
                                                                                                <Typography
                                                                                                    sx={{
                                                                                                        textAlign: 'center',
                                                                                                        // eslint-disable-next-line no-nested-ternary
                                                                                                        color:
                                                                                                            // eslint-disable-next-line no-nested-ternary
                                                                                                            metric?.percentage <= 34
                                                                                                                ? '#F44336'
                                                                                                                : metric.percentage >= 35 &&
                                                                                                                  metric.percentage <= 80
                                                                                                                ? '#ffe923'
                                                                                                                : '#02f13a',
                                                                                                        '& > svg': {
                                                                                                            width: 30,
                                                                                                            height: 30
                                                                                                        }
                                                                                                    }}
                                                                                                >
                                                                                                    {
                                                                                                        // eslint-disable-next-line no-nested-ternary
                                                                                                        metric?.percentage <= 34 ? (
                                                                                                            <ArrowDownwardIcon />
                                                                                                        ) : metric.percentage >= 35 &&
                                                                                                          metric.percentage <= 80 ? (
                                                                                                            <Brightness1Icon />
                                                                                                        ) : (
                                                                                                            <ArrowUpwardIcon />
                                                                                                        )
                                                                                                    }
                                                                                                </Typography>
                                                                                                <Typography variant="h3">{`${metric.percentage.toFixed(
                                                                                                    2
                                                                                                )} %`}</Typography>
                                                                                            </Stack>
                                                                                        </Grid>
                                                                                        <Grid item sm={12}>
                                                                                            <Button variant="outlined">
                                                                                                {' '}
                                                                                                <FormattedMessage id="details" />{' '}
                                                                                            </Button>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                </Grid>
                                                                                <Grid item xs={6} sx={{ bgcolor: 'light', p: 1 }}>
                                                                                    <ReactApexChart
                                                                                        options={optionsBars(metric)}
                                                                                        series={[metric?.percentage.toFixed(1)]}
                                                                                        type="radialBar"
                                                                                        height={170}
                                                                                    />
                                                                                </Grid>
                                                                            </Grid>
                                                                        </CardContent>
                                                                    </CardActionArea>
                                                                </Card>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                                <>
                                                    <Grid item xs={12} lg={8} md={12} sm={12}>
                                                        <Card
                                                            sx={{
                                                                background:
                                                                    theme.palette.mode === 'dark'
                                                                        ? theme.palette.dark.main
                                                                        : theme.palette.grey[50],
                                                                border: '1px solid',
                                                                borderColor:
                                                                    theme.palette.mode === 'dark' ? 'transparent' : theme.palette.grey[100],
                                                                '&:hover': {
                                                                    border: `1px solid${theme.palette.primary.main}`
                                                                }
                                                            }}
                                                        >
                                                            <CardHeader
                                                                title={<FormattedMessage id="latest-updates" />}
                                                                action={<DownloadUpdates dataToDownload={first?.skuCatalog} />}
                                                            />
                                                            <Divider />
                                                            <CardContent sx={{ p: 0, py: 1 }}>
                                                                <TableUpdates updates={first?.skuCatalog} />
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                </>
                                            </Grid>
                                        </TabPanel>
                                    </Box>
                                ))}
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                            <Grid container sx={{ pt: 0 }} spacing={3}>
                                {multiHealth?.length &&
                                    multiHealth.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((merchant: any) => (
                                        <Grid item xs={12} sm={6} key={merchant.merchantId}>
                                            <MultiCatalog merchant={merchant} />
                                        </Grid>
                                    ))}
                            </Grid>
                            <TablePagination
                                rowsPerPageOptions={[2, 4]}
                                component="div"
                                count={multiHealth.length || 1}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TabPanel>
                    </Stack>
                </Collapse>
            </Stack>
        </MainCard>
    );
};

export default FirstLevel;
